import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, errorText } from "@/lib/api";
import { useSiteChrome, siteHome } from "@/lib/site";
import { StageBar, UploadStage, DetailsStage } from "@/components/site/studio/StudioSteps";
import { ResumeDocument, ChangeLog } from "@/components/site/studio/StudioResult";
import { CourseMatch } from "@/components/site/studio/CourseMatch";
import { trackConversion } from "@/lib/track";

const SAVED = "aor_studio_last";
const blank = { name: "", email: "", target_role: "", job_description: "", missing_achievements: "", emphasis: "", consent: false, save_local: false, ph: null };

const Generating = ({ role }) => (
  <section className="builderCard" data-testid="studio-generating" style={{ textAlign: "center", padding: "56px 24px" }}>
    <span className="spinner" style={{ display: "inline-block", width: 34, height: 34, borderColor: "#0f7c7833", borderTopColor: "#0f7c78" }} />
    <h2 style={{ font: "500 30px/1.15 Georgia", margin: "18px 0 8px" }}>Building your {role || "optimized"} resume…</h2>
    <p style={{ color: "#657068", maxWidth: 520, margin: "0 auto" }}>We're auditing your resume, matching keywords to the role and rewriting bullets around evidence you already have. This usually takes under a minute. You'll also get it by email.</p>
  </section>
);

const Result = ({ doc, onRestart }) => (
  <section className="resultStage" data-testid="studio-step-3">
    <div className="resultToolbar">
      <div><p className="stepTag">STEP 3 OF 3</p><h2>Your optimized resume is ready.</h2></div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <a className="toolbarButton docxButton" href={doc.downloads?.docx} data-testid="studio-download-docx">Download DOCX</a>
        <a className="toolbarButton" href={doc.downloads?.pdf} data-testid="studio-download-pdf">Download PDF</a>
        <a className="toolbarButton" href={doc.downloads?.txt} data-testid="studio-download-txt" style={{ background: "#5c6b66" }}>Plain text</a>
      </div>
    </div>
    <div className="truthBanner"><b>Check every line.</b><span>AI can make mistakes. Confirm dates, titles, metrics, links and claims before you use this resume. Use the DOCX for ATS upload portals.</span></div>
    <div className="studioResultGrid" style={{ display: "grid", gap: 28, marginTop: 24 }}>
      <div style={{ overflowX: "auto", padding: "4px 0 20px" }}><ResumeDocument r={doc.resume} /></div>
      <ChangeLog cl={doc.resume?.change_log} />
    </div>
    <CourseMatch role={doc.target_role} />
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
      <button className="textButton" onClick={onRestart} data-testid="studio-restart-btn">← Tailor for another role</button>
      <Link className="textButton" to={siteHome()} data-testid="studio-home-link">Run the free resume check</Link>
    </div>
  </section>
);

export default function ResumeStudio() {
  useSiteChrome("Resume Studio | Art of Resume");
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [text, setText] = useState(() => sessionStorage.getItem("aor_resume_text") || "");
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(blank);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saveLocal = useRef(false);

  useEffect(() => {
    const id = params.get("r"), token = params.get("t");
    const saved = JSON.parse(localStorage.getItem(SAVED) || "null");
    const ref = id && token ? { id, token } : saved;
    if (!ref?.id) return;
    axios.get(`${API}/resume-studio/${ref.id}`, { params: { token: ref.token } })
      .then((r) => { if (r.data?.status === "ready") { setDoc(r.data); setStep(3); } })
      .catch(() => localStorage.removeItem(SAVED));
  }, [params]);

  useEffect(() => {
    if (step !== 3 || doc?.status !== "generating") return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/resume-studio/${doc.id}`, { params: { token: doc.token } });
        if (r.data?.status === "ready") {
          setDoc(r.data);
          trackConversion("resume", { transaction_id: r.data.id });
          if (saveLocal.current) localStorage.setItem(SAVED, JSON.stringify({ id: r.data.id, token: r.data.token }));
        } else if (r.data?.status === "failed") { setError(r.data.error); setDoc(null); setStep(2); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [step, doc]);

  const generate = async () => {
    setBusy(true); setError("");
    saveLocal.current = form.save_local;
    try {
      const { save_local, ph, ...body } = form;
      const r = await axios.post(`${API}/resume-studio/generate`, { ...body, phone: ph?.phone, phone_token: ph?.token, resume_text: text });
      setDoc(r.data); setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="antialiased" data-testid="resume-studio-page">
      <main className="builderPage">
        <nav className="nav shell">
          <Link to={siteHome()} className="brand" data-testid="studio-brand"><span className="peacockMark" aria-hidden="true" /> Art of Resume</Link>
          <div className="builderNav"><span>Private beta</span><Link to={siteHome()} data-testid="studio-nav-analyze">Analyze my resume</Link></div>
        </nav>
        <section className="builderShell shell">
          <header className="builderHeader">
            <p className="eyebrow">AI RESUME STUDIO</p>
            <h1>Upload your resume.<br /><em>Leave with a stronger one.</em></h1>
            <p>We preserve your facts, sharpen your evidence, and tailor the story to the role—without inventing achievements.</p>
            <div className="builderTrust"><span>✓ PDF, DOCX or TXT</span><span>✓ Evidence-first rewriting</span><span>✓ Professional ATS layout</span></div>
          </header>
          <StageBar step={step} />
          {step === 1 && <UploadStage text={text} setText={setText} file={file} setFile={setFile} onNext={() => setStep(2)} />}
          {step === 2 && <DetailsStage form={form} set={(k, v) => setForm((f) => ({ ...f, [k]: v }))} onBack={() => setStep(1)} onGenerate={generate} busy={busy} error={error} />}
          {step === 3 && (doc?.status === "ready" ? <Result doc={doc} onRestart={() => { setDoc(null); setStep(2); localStorage.removeItem(SAVED); }} /> : <Generating role={doc?.target_role} />)}
        </section>
      </main>
    </div>
  );
}
